import { useLocale as useLocaleR42 } from '@/lib/locale';
/**
 * SentryAppBoundary — Top-level error boundary for SplashPad.
 *
 * Wraps the entire <App /> tree so any uncaught render error becomes a
 * Sentry issue AND the user sees a branded "Something went wrong" card
 * with a Try again / Reload buttons instead of a white screen.
 *
 * When Sentry isn't configured (no DSN env var), this still acts as a
 * plain React error boundary — the only difference is that the error
 * isn't reported anywhere except the browser console.
 */
import React from 'react';
import * as Sentry from '@sentry/react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

function ErrorFallback({ error, eventId, resetError }) {
    const { t: t42 } = useLocaleR42();
    const details = [
        error?.message ? `Error: ${String(error.message).slice(0, 300)}` : null,
        eventId ? `Report ID: ${eventId}` : null,
        typeof window !== 'undefined' ? `Page: ${window.location.pathname}` : null,
        `Time: ${new Date().toLocaleString()}`,
    ].filter(Boolean).join('\n');
    return (
        <div
            className="min-h-screen w-full flex items-center justify-center bg-[#f4f7fb] p-4"
            data-testid="app-error-boundary"
        >
            <Card className="max-w-md w-full p-6 space-y-4 border-red-200 shadow-[0_18px_40px_rgba(220,38,38,0.12)]">
                <div className="flex items-center gap-2">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                    </span>
                    <h1 className="font-display text-xl font-bold text-red-700"> {t42("Something went wrong")} </h1>
                </div>
                <p className="text-sm text-[#1a3a5c]/90"> {t42("This page hit an unexpected error — it wasn't caused by anything you did, and our team has been notified automatically.")} </p>
                <ol className="list-decimal space-y-1 pl-5 text-sm text-[#1a3a5c]/80">
                    <li>{t42("Tap")} <strong>{t42("Try again")}</strong> {t42("— that fixes it most of the time.")}</li>
                    <li>{t42("Still stuck?")} <strong>{t42("Screenshot this card")}</strong> {t42("(including the box below) and send it to us in the support chat — the Report ID lets us find the exact problem instantly.")}</li>
                </ol>
                <pre
                    className="text-xs bg-red-50 border border-red-200 rounded-lg p-2.5 overflow-auto max-h-32 whitespace-pre-wrap text-red-800"
                    data-testid="app-error-message"
                >
                    {details}
                </pre>
                <div className="flex gap-2 pt-1">
                    <Button
                        onClick={resetError}
                        className="sd-cta sd-focus h-10 rounded-xl"
                        data-testid="app-error-retry"
                    >
                        <RefreshCw className="mr-1 h-4 w-4" /> {t42("Try again")} </Button>
                    <Button
                        variant="outline"
                        onClick={() => window.location.reload()}
                        className="h-10 rounded-xl"
                        data-testid="app-error-reload"
                    > {t42("Reload page")} </Button>
                </div>
            </Card>
        </div>
    );
}

export default function SentryAppBoundary({ children }) {
    return (
        <Sentry.ErrorBoundary
            // eslint-disable-next-line react/no-unstable-nested-components -- Sentry's fallback API expects a render function; the "component" only mounts once when caught.
            fallback={({ error, eventId, resetError }) => (
                <ErrorFallback error={error} eventId={eventId} resetError={resetError} />
            )}
            beforeCapture={(scope) => {
                scope.setTag('section', 'app_root');
            }}
        >
            {children}
        </Sentry.ErrorBoundary>
    );
}
